import { PostgrestResponse } from '@supabase/supabase-js';
import { ITipoContrato } from '../../../types/tables/ITipoContrato';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * Serviço para gerenciamento de tipos de contrato
 * Como tipo_contrato é uma tabela de configuração geral, não possui multi-tenancy
 */
export class TipoContratoService extends BaseMultiTenantService<ITipoContrato> {
  constructor() {
    super('tipo_contrato', ['descricao'])
  }

  /**
   * Busca tipos de contrato ativos
   */
  async getAtivos(): Promise<PostgrestResponse<ITipoContrato[]>> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select('*')
      .eq('ativo', true)
      .is('deleted_at', null)
      .order('descricao')

    if (error) {
      throw new Error(`Erro ao buscar tipos de contrato ativos: ${error.message}`)
    }

    return { data: data || [], error } as PostgrestResponse<ITipoContrato[]>
  }

  /**
   * Busca tipo de contrato por código
   */
  async getByCodigo(codigo: string): Promise<ITipoContrato | null> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select('*')
      .eq('codigo', codigo)
      .eq('ativo', true)
      .is('deleted_at', null)
      .single()

    if (error && error.code !== 'PGRST116') { // PGRST116 = No rows found
      throw new Error(`Erro ao buscar tipo de contrato por código: ${error.message}`)
    }

    return data || null
  }

  /**
   * Busca tipos que requerem assinatura eletrônica
   */
  async getComAssinaturaEletronica(): Promise<PostgrestResponse<ITipoContrato[]>> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select('*')
      .eq('requer_assinatura_eletronica', true)
      .eq('ativo', true)
      .is('deleted_at', null)
      .order('descricao')

    if (error) {
      throw new Error(`Erro ao buscar tipos com assinatura eletrônica: ${error.message}`)
    }

    return { data: data || [], error } as PostgrestResponse<ITipoContrato[]>
  }

  /**
   * Busca tipos obrigatórios
   */
  async getObrigatorios(): Promise<PostgrestResponse<ITipoContrato[]>> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select('*')
      .eq('obrigatorio', true)
      .eq('ativo', true)
      .is('deleted_at', null)
      .order('ordem', { ascending: true })

    if (error) {
      throw new Error(`Erro ao buscar tipos obrigatórios: ${error.message}`)
    }

    return { data: data || [], error } as PostgrestResponse<ITipoContrato[]>
  }
}

export const tipoContratoService = new TipoContratoService()
